import React, { useState, useEffect } from 'react';
import { View, Text, Button } from "../../Components/Themed";
import type { RouterLeagueProps, RouterContestProps } from '../types';
import { FlatList } from 'react-native';
import { useColors } from '../../constants/useColors';
import DropDown from '../../Components/Dropdown';
import { SearchBox } from '../../Components';
import Pagination from '../../Components/Pagination';
import moment from 'moment-mini';

type ContestSelectorProps = {
    leagues: RouterLeagueProps[];
    selectedLeague: RouterLeagueProps | null;
    contests: RouterContestProps[];
    selectedContest: RouterContestProps | null;
    onSelectLeague: (league: RouterLeagueProps) => void;
    onSelectContest: (contest: RouterContestProps) => void;
};

const ContestSelector = ({
    leagues,
    selectedLeague,
    contests,
    selectedContest,
    onSelectLeague,
    onSelectContest
}: ContestSelectorProps) => {
    const Colors = useColors();
    const [contestSearchText, setContestSearchText] = useState('');
    const [contestOffset, setContestOffset] = useState(0);
    const contestsPerPage = 10;

    // Reset pagination when league or search changes
    useEffect(() => {
        setContestOffset(0);
    }, [selectedLeague, contestSearchText]);

    // Filter and paginate contests
    const searchFilteredContests = contestSearchText.trim()
        ? contests.filter(c =>
            c.contest_label.toLowerCase().includes(contestSearchText.toLowerCase())
          )
        : contests;

    const paginatedContests = searchFilteredContests.slice(
        contestOffset * contestsPerPage,
        (contestOffset * contestsPerPage) + contestsPerPage
    );

    const totalContestPages = Math.ceil(searchFilteredContests.length / contestsPerPage);

    return (
        <View float style={{ margin: 10, flex: 1 }}>
            <View type='header' style={{
                padding: 10,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8
            }}>
                <Text theme='h1' style={{ fontSize: 16 }}>Contests</Text>
                <Text theme='description' style={{ fontSize: 12, marginTop: 3 }}>
                    {searchFilteredContests.length} of {contests.length} available
                </Text>
            </View>
            <View type='body' style={{ flex: 1 }}>
                {/* League Selector */}
                <View transparent style={{ padding: 10, borderBottomWidth: 1, borderBottomColor: Colors.borders.light }}>
                    <DropDown
                        selected_value={selectedLeague?.name || 'Select League'}
                        dropdown_options={leagues.map(league => ({
                            value: league.name,
                            eligible_options: [league.name]
                        }))}
                        onOptionSelect={(leagueName) => {
                            const league = leagues.find(l => l.name === leagueName);
                            if (league) {
                                onSelectLeague(league);
                                setContestSearchText('');
                                setContestOffset(0);
                            }
                        }}
                    />
                </View>

                {!selectedLeague ? (
                    <View transparent style={{ padding: 20, alignItems: 'center' }}>
                        <Text theme='description' style={{ fontSize: 12, textAlign: 'center' }}>
                            Select a league to view contests
                        </Text>
                    </View>
                ) : contests.length === 0 ? (
                    <View transparent style={{ padding: 20, alignItems: 'center' }}>
                        <Text theme='description' style={{ fontSize: 12, textAlign: 'center' }}>
                            No contests available for this league
                        </Text>
                    </View>
                ) : (
                    <>
                        {/* Search Box */}
                        <View transparent style={{ padding: 10, borderBottomWidth: 1, borderBottomColor: Colors.borders.light }}>
                            <SearchBox
                                onChange={(text) => {
                                    setContestSearchText(text);
                                    setContestOffset(0);
                                }}
                                hide_search_button
                            />
                        </View>

                        {/* Pagination */}
                        {searchFilteredContests.length > contestsPerPage && (
                            <View transparent style={{ padding: 10, borderBottomWidth: 1, borderBottomColor: Colors.borders.light }}>
                                <Pagination
                                    offset={contestOffset}
                                    onNext={() => {
                                        if ((contestOffset + 1) < totalContestPages) {
                                            setContestOffset(contestOffset + 1);
                                        }
                                    }}
                                    onPrevious={() => {
                                        if (contestOffset > 0) {
                                            setContestOffset(contestOffset - 1);
                                        }
                                    }}
                                />
                            </View>
                        )}

                        {/* Contest List */}
                        {searchFilteredContests.length === 0 ? (
                            <View transparent style={{ padding: 20, alignItems: 'center' }}>
                                <Text theme='description' style={{ fontSize: 12, textAlign: 'center' }}>
                                    No contests match your search
                                </Text>
                            </View>
                        ) : (
                            <FlatList
                                data={paginatedContests}
                                keyExtractor={(item) => item.router_contest_id}
                                renderItem={({ item }) => (
                                    <Button
                                        transparent
                                        style={{
                                            padding: 10,
                                            borderBottomWidth: 1,
                                            borderBottomColor: Colors.borders.light,
                                            backgroundColor: selectedContest?.router_contest_id === item.router_contest_id
                                                ? Colors.views.header
                                                : 'transparent'
                                        }}
                                        onPress={() => onSelectContest(item)}
                                    >
                                        <View transparent style={{ flex: 1 }}>
                                            <Text
                                                theme='h1'
                                                style={{ fontSize: 13 }}
                                                numberOfLines={2}
                                            >
                                                {item.contest_label}
                                            </Text>
                                            <Text
                                                theme='description'
                                                style={{ fontSize: 11, marginTop: 2 }}
                                                numberOfLines={1}
                                            >
                                                {moment(item.scheduled_datetime).format('ddd, MMM D [at] h:mm A')}
                                            </Text>
                                        </View>
                                    </Button>
                                )}
                            />
                        )}
                    </>
                )}
            </View>
        </View>
    );
};

export default ContestSelector;
